import {
  fetchLaborCrewsForRegion,
  fetchMFCodesUncached,
  fetchProductLaborCrews,
  fetchProducts,
  fetchUFCodesUncached,
} from "../../api";
import { fetchGCGRDetailGroupsUncached } from "../../api/gcgrs";
import { track } from "../../clients/mixpanel";
import { ValidationError, handleError } from "../../errors";
import { ProductData, RegionalUnitCost, excludedKeys } from "../../interfaces/Product";
import { omitKeys } from "../utils/omitKeys";
import {
  buildSortFieldColumnNameMap,
  createSortCodeLookup,
  getSortFieldsWithCodes,
  unpackAllSortCodes,
} from "../datasources/sortCodes";
import { MFUFlookupByDepth } from "../utils/sortCodes";
import { loadDataIntoSheet } from "../workbook";

export const loadUPCData = async (regionId?: string, regionName?: string): Promise<void> => {
  const startTime = performance.now();
  try {
    const productsResult = await fetchProducts({
      populate: ["owner", ...(regionId ? ["regional_unit_costs"] : [])],
      regionId,
    });
    if (!productsResult.success) {
      throw productsResult.error;
    }
    const productsData: ProductData[] = productsResult.data || [];
    if (productsData.length === 0) {
      throw new ValidationError("No UPC products found");
    }

    let groupsData: Record<string, any>[] = [];
    const groupsRes = await fetchGCGRDetailGroupsUncached();
    if (groupsRes.success) {
      groupsData = groupsRes.data || [];
    } else {
      console.warn(
        "UPC load: could not fetch GCGR detail groups; gcgr_group will show raw IDs until groups load.",
        groupsRes.error
      );
    }

    const crewColumns = regionId ? await fetchRegionalCrewColumns(regionId, regionName || "") : undefined;

    productsData.forEach((product) => {
      if (crewColumns) Object.assign(product, crewColumns(product.id));
      if (regionId && product.regional_unit_costs) {
        const matchingRegionalCost = product.regional_unit_costs.find(
          (cost: RegionalUnitCost) => cost.region === regionId
        );
        if (matchingRegionalCost) {
          Object.assign(product, {
            [`${regionName || ""}_total_uc`]: matchingRegionalCost.total_uc,
            [`${regionName || ""}_labor_uc`]: matchingRegionalCost.labor_uc,
            [`${regionName || ""}_prod_rate`]: matchingRegionalCost.prod_rate,
            [`${regionName || ""}_labor_rate`]: matchingRegionalCost.labor_rate,
            [`${regionName || ""}_material_uc`]: matchingRegionalCost.material_uc,
            [`${regionName || ""}_sub_uc`]: matchingRegionalCost.sub_uc,
            [`${regionName || ""}_equip_uc`]: matchingRegionalCost.equip_uc,
            [`${regionName || ""}_other_uc`]: matchingRegionalCost.other_uc,
          });
        }
      }
    });

    const [ufCodeResult, mfCodeResult] = await Promise.all([fetchUFCodesUncached(), fetchMFCodesUncached()]);
    if (!ufCodeResult.success) {
      throw ufCodeResult.error;
    }
    if (!mfCodeResult.success) {
      throw mfCodeResult.error;
    }
    const mfCodes = mfCodeResult.data || [];
    const ufCodes = ufCodeResult.data || [];
    const mfLookup = createSortCodeLookup(mfCodes);
    const ufLookup = createSortCodeLookup(ufCodes);

    const uniqueSortFieldIds = new Set<string>();
    productsData.forEach((product) => {
      if (product.extras) {
        Object.keys(product.extras).forEach((id) => uniqueSortFieldIds.add(id));
      }
    });
    const usedSortFields = await getSortFieldsWithCodes(Array.from(uniqueSortFieldIds), undefined, { fresh: true });
    const sortFieldColumnNameMap = buildSortFieldColumnNameMap(usedSortFields);

    await tabulateUPCData(productsData, usedSortFields, ufLookup, mfLookup, sortFieldColumnNameMap, groupsData);
  } catch (error) {
    console.error("Error fetching UPC data:", error);
    // Preserve domain errors (ValidationError, UnauthorizedError) and add context
    throw handleError(error, { regionId, regionName, context: "loadUPCData" });
  } finally {
    track("excel.upc.fetch", {
      loadTime: (performance.now() - startTime) / 1000,
    });
  }
};

/**
 * The region's crew columns for each product: its crew's name, Individual Rate (IBR) and crew size.
 * Every product gets them, blank without a crew, so the columns don't come and go with the data. A
 * failed fetch leaves the columns out rather than showing every product as crewless.
 */
const fetchRegionalCrewColumns = async (
  regionId: string,
  regionName: string
): Promise<((productId: string) => Record<string, string | number | null>) | undefined> => {
  const [assignmentsResult, crewsResult] = await Promise.all([
    fetchProductLaborCrews(regionId),
    fetchLaborCrewsForRegion(regionId),
  ]);
  if (!assignmentsResult.success || !crewsResult.success) {
    console.warn(
      "UPC load: could not fetch labor crews; the crew columns are left out.",
      assignmentsResult.success ? undefined : assignmentsResult.error,
      crewsResult.success ? undefined : crewsResult.error
    );
    return undefined;
  }
  const assignmentByProduct = new Map((assignmentsResult.data || []).map((a) => [a.product, a]));
  const crewSizeById = new Map((crewsResult.data || []).map((crew) => [crew.id, crew.crew_size]));
  return (productId) => {
    const assignment = assignmentByProduct.get(productId);
    return {
      [`${regionName}_labor_crew`]: assignment?.labor_crew_description ?? "",
      [`${regionName}_individual_rate`]: assignment ? assignment.ibr : null,
      [`${regionName}_crew_size`]: assignment ? (crewSizeById.get(assignment.labor_crew) ?? null) : null,
    };
  };
};

const tabulateUPCData = async (
  products: ProductData[],
  usedSortFields: { [key: string]: any },
  ufLookup: MFUFlookupByDepth,
  mfLookup: MFUFlookupByDepth,
  sortFieldColumnNameMap: Record<string, string>,
  groups: Record<string, any>[]
): Promise<void> => {
  try {
    const tabulatedData = products.map((product) => {
      const ownerName = product.owner?.full_name || "";
      const ownerEmail = product.owner?.email || "";
      const groupName =
        product.gcgr_group != null ? (groups.find((g) => g.id === product.gcgr_group)?.name ?? product.gcgr_group) : "";
      const record = {
        ...product,
        owner_name: ownerName,
        owner_email: ownerEmail,
        gcgr_group: groupName,
      };

      // Unpack all sort codes (mf, uf, extras) directly on the record
      unpackAllSortCodes(record, mfLookup, ufLookup, usedSortFields, sortFieldColumnNameMap);

      // Now filter out unwanted keys after all processing is complete
      return omitKeys(record, Array.from(excludedKeys) as readonly (keyof typeof record)[]);
    });

    await loadDataIntoSheet(tabulatedData, "UPC", { adminTable: true });
  } catch (error) {
    console.error("Error with UPC ETL:", error);
    throw error;
  }
};
